import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { TopBar } from "../components/Navigation";
import EditProfile from "./EditProfile";
import PrivacySettings from "./PrivacySettings";
import NotificationSettings from "./NotificationSettings";
import EmailSettings from "./EmailSettings";
import SecuritySettings from "./settings/SecuritySettings";
import { SettingsDirtyProvider, useSettingsDirty } from "./settings/SettingsDirtyContext";
import {
    Settings as SettingsIcon, User, Shield, Bell, Mail, CreditCard, Crown, ArrowRight, Fingerprint,
} from "lucide-react";

const TABS = [
    { id: "profile", label: "Profile", icon: User, description: "Name, bio, photo, gear, and preferences" },
    { id: "privacy", label: "Privacy", icon: Shield, description: "Who can see what" },
    { id: "security", label: "Security", icon: Fingerprint, description: "Passkeys and sign-in" },
    { id: "notifications", label: "Notifications", icon: Bell, description: "What chimes the bell" },
    { id: "emails", label: "Emails", icon: Mail, description: "Inbox preferences" },
    { id: "billing", label: "Billing", icon: CreditCard, description: "Plan and subscription" },
];

const VALID_TAB_IDS = new Set(TABS.map((t) => t.id));

export default function Settings() {
    return (
        <SettingsDirtyProvider>
            <SettingsInner />
        </SettingsDirtyProvider>
    );
}

function SettingsInner() {
    const { user } = useAuth();
    const { dirtyTabs, saveAll } = useSettingsDirty();
    const [savingAll, setSavingAll] = useState(false);
    const [params, setParams] = useSearchParams();
    const requested = params.get("tab");
    const initialTab = VALID_TAB_IDS.has(requested) ? requested : "profile";
    const [tab, setTab] = useState(initialTab);
    // Tabs lazy-mount on first click and stay mounted afterward — preserves
    // unsaved changes when the user moves between sections.
    const [mounted, setMounted] = useState(() => new Set([initialTab]));

    useEffect(() => {
        setMounted((m) => {
            if (m.has(tab)) return m;
            const next = new Set(m);
            next.add(tab);
            return next;
        });
        if (params.get("tab") !== tab) {
            const next = new URLSearchParams(params);
            next.set("tab", tab);
            setParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    // If the URL changes externally (e.g., redirect from /settings/privacy)
    // mirror it back into the local tab state.
    useEffect(() => {
        const t = params.get("tab");
        if (t && VALID_TAB_IDS.has(t) && t !== tab) setTab(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    return (
        <div>
            <TopBar title="Settings" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <div className="hidden lg:flex items-center gap-3 mb-6">
                    <SettingsIcon className="w-8 h-8 text-ember-orange" />
                    <div>
                        <h1 className="font-display font-black text-4xl text-midnight-pine tracking-tight">
                            Settings
                        </h1>
                        <p className="text-slate-gray">
                            Account, privacy, notifications, and billing — all in one place.
                        </p>
                    </div>
                </div>

                {dirtyTabs.size > 0 && (
                    <div
                        data-testid="settings-dirty-banner"
                        role="status"
                        className="mb-4 px-4 py-3 rounded-md bg-ember-orange/10 border border-ember-orange/40 text-ember-dark flex items-center gap-3 flex-wrap"
                    >
                        <span className="w-2 h-2 rounded-full bg-ember-orange animate-pulse shrink-0" />
                        <span className="text-sm font-semibold flex-1 min-w-[200px]">
                            You have unsaved changes in{" "}
                            {Array.from(dirtyTabs)
                                .map((id) => TABS.find((t) => t.id === id)?.label || id)
                                .join(", ")}
                            . Save or discard before leaving Settings.
                        </span>
                        {dirtyTabs.size >= 2 && (
                            <button
                                data-testid="settings-save-all-btn"
                                disabled={savingAll}
                                onClick={async () => {
                                    setSavingAll(true);
                                    try {
                                        const ids = Array.from(dirtyTabs);
                                        const results = await saveAll(ids);
                                        const failed = results.filter((r) => !r.ok);
                                        if (failed.length === 0) {
                                            toast.success(`Saved ${results.length} sections`);
                                        } else if (failed.length === results.length) {
                                            // All toasts already fired by individual saves
                                        } else {
                                            const okLabels = results
                                                .filter((r) => r.ok)
                                                .map((r) => TABS.find((t) => t.id === r.id)?.label || r.id)
                                                .join(", ");
                                            toast.success(`Saved ${okLabels}; ${failed.length} failed — see other toasts.`);
                                        }
                                    } finally {
                                        setSavingAll(false);
                                    }
                                }}
                                className="px-4 py-1.5 rounded-md bg-ember-orange text-white text-xs font-bold hover:bg-ember-dark active:scale-95 disabled:opacity-60 transition-all whitespace-nowrap"
                            >
                                {savingAll ? "Saving…" : `Save all (${dirtyTabs.size})`}
                            </button>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
                    <SettingsRail tab={tab} onChange={setTab} user={user} dirtyTabs={dirtyTabs} />
                    <div data-testid={`settings-tab-content-${tab}`} className="min-w-0">
                        {/* Each tab is rendered when first visited and kept mounted thereafter so
                            the user doesn't lose unsaved changes when switching sections. */}
                        <div className={tab === "profile" ? "" : "hidden"} aria-hidden={tab !== "profile"}>
                            {mounted.has("profile") && <EditProfile embedded />}
                        </div>
                        <div className={tab === "privacy" ? "" : "hidden"} aria-hidden={tab !== "privacy"}>
                            {mounted.has("privacy") && <PrivacySettings embedded />}
                        </div>
                        <div className={tab === "security" ? "" : "hidden"} aria-hidden={tab !== "security"}>
                            {mounted.has("security") && <SecuritySettings />}
                        </div>
                        <div className={tab === "notifications" ? "" : "hidden"} aria-hidden={tab !== "notifications"}>
                            {mounted.has("notifications") && <NotificationSettings embedded />}
                        </div>
                        <div className={tab === "emails" ? "" : "hidden"} aria-hidden={tab !== "emails"}>
                            {mounted.has("emails") && <EmailSettings embedded />}
                        </div>
                        <div className={tab === "billing" ? "" : "hidden"} aria-hidden={tab !== "billing"}>
                            {mounted.has("billing") && <BillingTab user={user} />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SettingsRail({ tab, onChange, user, dirtyTabs }) {
    const isDirty = (id) => dirtyTabs?.has(id);
    return (
        <aside className="min-w-0 w-full lg:sticky lg:top-24 lg:self-start">
            {/* Mobile horizontal tab strip */}
            <div className="lg:hidden flex gap-1 bg-snowcap border border-ridge-line rounded-lg p-1 mb-4 overflow-x-auto">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        data-testid={`settings-tab-mobile-${t.id}`}
                        onClick={() => onChange(t.id)}
                        className={`px-3 py-2 rounded-md text-xs font-bold whitespace-nowrap flex items-center gap-1.5 transition-all ${
                            tab === t.id ? "bg-midnight-pine text-white" : "text-slate-gray hover:text-granite"
                        }`}
                    >
                        <t.icon className="w-3.5 h-3.5" /> {t.label}
                        {isDirty(t.id) && (
                            <span
                                data-testid={`settings-tab-dirty-mobile-${t.id}`}
                                className="w-1.5 h-1.5 rounded-full bg-ember-orange"
                                aria-label="unsaved changes"
                            />
                        )}
                    </button>
                ))}
            </div>

            {/* Desktop vertical rail */}
            <nav className="hidden lg:block bg-snowcap border border-ridge-line rounded-lg p-2">
                <ul className="space-y-0.5">
                    {TABS.map((t) => (
                        <li key={t.id}>
                            <button
                                data-testid={`settings-tab-${t.id}`}
                                onClick={() => onChange(t.id)}
                                className={`w-full text-left px-3 py-2.5 rounded-md flex items-start gap-3 transition-all ${
                                    tab === t.id
                                        ? "bg-midnight-pine text-white"
                                        : "text-granite hover:bg-alpine-mist"
                                }`}
                            >
                                <t.icon className={`w-4 h-4 mt-0.5 shrink-0 ${tab === t.id ? "text-ember-orange" : "text-slate-gray"}`} />
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-bold leading-tight flex items-center gap-2">
                                        {t.label}
                                        {isDirty(t.id) && (
                                            <span
                                                data-testid={`settings-tab-dirty-${t.id}`}
                                                title="Unsaved changes"
                                                className="w-1.5 h-1.5 rounded-full bg-ember-orange"
                                                aria-label="unsaved changes"
                                            />
                                        )}
                                    </div>
                                    <div className={`text-[11px] leading-snug mt-0.5 ${tab === t.id ? "text-white/70" : "text-slate-gray"}`}>
                                        {t.description}
                                    </div>
                                </div>
                            </button>
                        </li>
                    ))}
                </ul>
            </nav>

            <div className="mt-3 hidden lg:block px-3 text-[10px] text-slate-gray font-semibold uppercase tracking-widest">
                Signed in as
            </div>
            <div className="hidden lg:flex items-center gap-2 mt-1 px-3">
                <div className="w-8 h-8 rounded-full bg-trail-tan overflow-hidden flex items-center justify-center shrink-0">
                    {user?.avatar ? (
                        <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <User className="w-4 h-4 text-midnight-pine" />
                    )}
                </div>
                <div className="min-w-0">
                    <div className="text-xs font-bold text-granite truncate">{user?.name}</div>
                    <div className="text-[10px] text-slate-gray truncate capitalize">{user?.tier} member</div>
                </div>
            </div>
        </aside>
    );
}

function BillingTab({ user }) {
    const isPremium = user?.tier === "premium";
    return (
        <div data-testid="settings-billing" className="space-y-4">
            <section className="bg-snowcap border border-ridge-line rounded-lg p-6">
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                    <div>
                        <h2 className="font-display font-bold text-2xl text-midnight-pine mb-1">
                            Plan & subscription
                        </h2>
                        <p className="text-sm text-slate-gray">
                            Manage your RealWorld Outdoors plan, payment method, and invoices.
                        </p>
                    </div>
                    <div
                        data-testid="settings-billing-current-tier"
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest ${
                            isPremium ? "bg-ember-orange text-white" : "bg-alpine-mist text-granite"
                        }`}
                    >
                        <Crown className="w-3.5 h-3.5" /> {user?.tier || "free"} plan
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mb-5">
                    <PlanCard
                        plan="Free"
                        price="$0"
                        bullets={["Public adventures", "Up to 3 active marketplace listings", "Basic search"]}
                        active={user?.tier === "free"}
                    />
                    <PlanCard
                        plan="Premium"
                        price="$5/mo"
                        bullets={["Ad-free everywhere", "Private adventures + GPX uploads", "Priority support"]}
                        active={isPremium}
                        highlight
                    />
                </div>

                <Link
                    to="/pricing"
                    data-testid="settings-billing-go-pricing"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-midnight-pine text-white font-bold text-sm hover:bg-deep-evergreen active:scale-95 transition-all"
                >
                    {isPremium ? "Manage subscription" : "Upgrade to Premium"} <ArrowRight className="w-4 h-4" />
                </Link>
            </section>
        </div>
    );
}

function PlanCard({ plan, price, bullets, active, highlight }) {
    return (
        <div
            className={`rounded-lg p-4 border ${
                active
                    ? highlight
                        ? "border-ember-orange bg-ember-orange/5 ring-2 ring-ember-orange/20"
                        : "border-midnight-pine bg-midnight-pine/5"
                    : "border-ridge-line bg-alpine-mist/40"
            }`}
        >
            <div className="flex items-baseline justify-between mb-2">
                <span className="font-display font-bold text-lg text-midnight-pine">{plan}</span>
                <span className={`font-bold ${highlight ? "text-ember-orange" : "text-granite"}`}>{price}</span>
            </div>
            <ul className="space-y-1 text-xs text-slate-gray">
                {bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                        <span className="w-1 h-1 mt-1.5 rounded-full bg-ember-orange shrink-0" />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
            {active && (
                <div className="mt-3 inline-flex items-center px-2 py-0.5 rounded-full bg-deep-evergreen/15 text-deep-evergreen text-[10px] font-bold uppercase tracking-widest">
                    Current plan
                </div>
            )}
        </div>
    );
}
